/* eslint-disable @next/next/no-img-element */
import clsx from "clsx";
import { domToReact, HTMLReactParserOptions, Element } from "html-react-parser";
import Zoom from "react-medium-image-zoom";
import "react-medium-image-zoom/dist/styles.css";

export enum ParserOptions {
  Ignore = "ignore",
}

export const spacingClasses = "mb-4";
export const textClasses = "text-base";

export const spacingAndTextSizeClasses = clsx(spacingClasses, textClasses);

/* image */
export const imageContainerClasses = "flex justify-center";
export const imageClasses = "max-w-full max-h-600 select-none";

/* figure */
export const figureContainerClasses = clsx(
  spacingClasses,
  "relative flex flex-col items-center",
);
export const figureImageClasses = clsx(imageClasses, "mb-2");
export const figcaptionClasses =
  "text-center text-sm  text-gray-500 min-w-280 max-w-80p";

/* link */
export const linkClasses = clsx(textClasses, "text-primary hover:underline");

/* heading */
export const headingThreeClasses = clsx(
  spacingClasses,
  "text-20 md:text-2xl md:leading-9 font-medium",
);

/* video */
export const videoContainerClasses =
  "relative w-full aspect-w-16 aspect-h-9 my-2";
export const iframeClasses = "w-full h-full";

export const articlePostOptions: HTMLReactParserOptions = {
  replace: (node: Element) => {
    if (!node.attribs) return;

    const { name, attribs, children } = node;

    if (name === "iframe") {
      return (
        <div className={videoContainerClasses}>
          <iframe
            className={iframeClasses}
            src={attribs.src}
            aria-label="Youtube video"
            frameBorder="0"
          >
            {domToReact(children, articlePostOptions)}
          </iframe>
        </div>
      );
    }
    if (name === "h3") {
      return (
        <h3 className={headingThreeClasses}>
          {domToReact(children, articlePostOptions)}
        </h3>
      );
    }
    if (name === "p") {
      return (
        <p className={spacingAndTextSizeClasses}>
          {domToReact(children, articlePostOptions)}
        </p>
      );
    }
    if (name === "a") {
      return (
        <a
          className={linkClasses}
          href={attribs?.href}
          target="_blank"
          rel="noreferrer"
        >
          {domToReact(children, articlePostOptions)}
        </a>
      );
    }

    if (name === "img") {
      const figureElement = node?.parent?.parent;
      const figcaptionElement = figureElement?.children?.find(
        (child) => (child as Element).name === "figcaption",
      ) as Element;

      const alt =
        (figcaptionElement?.childNodes?.[0] as Element & { data?: string })
          ?.data ?? "";

      const isInFigure = !!figureElement;

      return (
        <div
          className={clsx(imageContainerClasses, !isInFigure && spacingClasses)}
        >
          <Zoom zoomMargin={16}>
            <img
              className={
                isInFigure && !!alt ? figureImageClasses : imageClasses
              }
              src={attribs?.src}
              alt={alt}
            />
          </Zoom>
        </div>
      );
    }

    if (name === "figure") {
      return (
        <figcaption className={figureContainerClasses}>
          {domToReact(children, articlePostOptions)}
        </figcaption>
      );
    }

    if (name === "figcaption") {
      return (
        <figcaption className={figcaptionClasses}>
          {domToReact(children, articlePostOptions)}
        </figcaption>
      );
    }

    if (attribs?.parse === ParserOptions.Ignore) {
      return <>{domToReact(children, articlePostOptions)}</>;
    }
  },
};

export const imageCardOptions: HTMLReactParserOptions = {
  replace: (node: Element) => {
    if (!node.attribs) return;

    const { attribs, children, name } = node;
    if (name === "h3") return <h3>{domToReact(children, imageCardOptions)}</h3>;
    if (name === "p") return <p>{domToReact(children, imageCardOptions)}</p>;
    if (name === "a")
      return (
        <a href={attribs?.href} target="_blank" rel="noreferrer">
          {domToReact(children, imageCardOptions)}
        </a>
      );
  },
};
